import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { API } from '@/api';
import { LoadingOverlay } from '@/components/elements/LoadingOverlay';
import { ShareDeviceReservationDialog } from '@/components/elements/ShareDeviceDialog';
import { ShareDeviceConfirmEmailDialogContent } from '@/components/elements/ShareDeviceDialogContentConfirmEmail';
import { ShareDeviceDialogContentFullyBooked } from '@/components/elements/ShareDeviceDialogContentFullyBooked';
import { ShareDeviceReservationSuccessDialogContent } from '@/components/elements/ShareDeviceDialogContentReservationSuccess';
import { ReservationFormFields } from '@/components/elements/ShareDeviceReservationFormFields';
import { ReservationIntro } from '@/components/elements/ShareDeviceReservationIntro';
import { ShareDeviceSidebar } from '@/components/elements/ShareDeviceSidebar';
import { SharedDeviceTimeoutNotification } from '@/components/elements/SharedDeviceTimeoutNotification';
import { ShareDeviceLayout } from '@/components/layouts/ShareDeviceLayout';
import { ROUTE_PATH } from '@/constants/routes';
import { SHARED_DEVICE_TIMEOUT_SECONDS } from '@/constants/sharedDevice';
import { RESERVABLE_TIME_UNIT_IN_MINUTES, SPACE_TYPE } from '@/constants/space';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { API_ENDPOINTS, useApiRequest } from '@/hooks/useApiRequest';
import { useConvertToDate } from '@/hooks/useCovertToDate';
import { useDialog } from '@/hooks/useDialog';
import { useShareDeviceReservationForm } from '@/hooks/useShareDeviceReservationForm';
import { useSharedDeviceTimeout } from '@/hooks/useSharedDeviceTimeout';

export const ShareDeviceUserInfo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { translateApiError } = useApiErrorTranslation();

  const selectedTime: string = location.state?.selectedTime;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reservationData, setReservationData] = useState<{
    selectedTime: string;
    reservation: API['POST']['CreateSharedDeviceReservation']['response']['reservation'];
    userInfo: {
      lastName: string;
      firstName: string;
      email: string;
    };
  }>();

  // カスタムフック利用
  const form = useShareDeviceReservationForm();
  const confirmDialog = useDialog();
  const successDialog = useDialog();
  const fullyBookedDialog = useDialog();
  const { formatToJstIso8601 } = useConvertToDate();

  // TOP画面に戻るタイムアウト機能
  const { remainingSeconds, resetTimer } = useSharedDeviceTimeout(
    SHARED_DEVICE_TIMEOUT_SECONDS.DEFAULT,
  );

  const { execute } = useApiRequest(
    'POST',
    API_ENDPOINTS.POST.CREATE_SHARED_DEVICE_RESERVATION,
    { showGlobalLoading: false },
  );

  const handleBackToTop = () => {
    navigate(ROUTE_PATH.SHARE_DEVICE);
  };

  const handleBackToPreviousPage = () => {
    navigate(ROUTE_PATH.SHARE_DEVICE_TIME_SELECTION);
  };

  useEffect(() => {
    if (!selectedTime) {
      navigate(ROUTE_PATH.SHARE_DEVICE_TIME_SELECTION);
    }
  }, [selectedTime, navigate]);

  const handleShowConfirmDialog = () => {
    if (!form.isValid()) {
      setErrorMessage(t('shareDevice.userInfo.validation.allFieldsRequired'));
      return;
    }
    setErrorMessage(null);
    confirmDialog.open();
  };

  const handleSuccessDialogConfirm = () => {
    successDialog.close();

    // 予約詳細画面に遷移
    if (reservationData) {
      navigate(ROUTE_PATH.SHARE_DEVICE_COMPLETE, {
        state: reservationData,
      });
    }
  };

  const handleFullyBookedDialogConfirm = () => {
    fullyBookedDialog.close();
    // 予約時間選択画面に遷移
    navigate(ROUTE_PATH.SHARE_DEVICE_TIME_SELECTION);
  };

  const handleReservation = async () => {
    confirmDialog.close();
    setIsLoading(true);

    try {
      // シャワールームの予約時間単位（分）を取得
      const durationMinutes =
        RESERVABLE_TIME_UNIT_IN_MINUTES[SPACE_TYPE.SHOWER_ROOM];
      const startTime = new Date(selectedTime);

      const endTime = new Date(
        startTime.getTime() + durationMinutes * 60 * 1000,
      );

      const result = await execute({
        reservation: {
          last_name: form.lastName,
          first_name: form.firstName,
          email: form.email,
          start_time: selectedTime,
          end_time: formatToJstIso8601(endTime),
          space_type: SPACE_TYPE.SHOWER_ROOM,
        },
      });

      if (result.ok) {
        // 予約データを保存
        setReservationData({
          selectedTime: selectedTime,
          reservation: result.data.reservation,
          userInfo: {
            lastName: form.lastName,
            firstName: form.firstName,
            email: form.email,
          },
        });

        // 成功ダイアログを表示
        successDialog.open();
      } else {
        console.error(result);
        console.error('Reservation failed:', result.error);

        const { message, isFullyBooked } = translateApiError(result.error);

        if (isFullyBooked) {
          // 満枠の場合
          fullyBookedDialog.open();
        } else {
          setErrorMessage(message);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ShareDeviceLayout showBackToTop onBackToTop={handleBackToTop}>
      {isLoading && (
        <LoadingOverlay message={t('shareDevice.userInfo.loading')} />
      )}

      <div className="page page_f3">
        <main className="page__main">
          <div className="page__content">
            <section className="f3">
              <div className="f3__line">
                <div className="f3__inner">
                  <div className="f3__area">
                    {errorMessage && (
                      <p className="f3__alert f3__alert--error">
                        {errorMessage}
                      </p>
                    )}
                    {selectedTime && (
                      <ReservationIntro selectedTime={selectedTime} />
                    )}

                    <div className="f3__form-section">
                      <form
                        className="f3__form"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <ReservationFormFields
                          lastName={form.lastName}
                          firstName={form.firstName}
                          email={form.email}
                          onLastNameChange={form.setLastName}
                          onFirstNameChange={form.setFirstName}
                          onEmailChange={form.setEmail}
                          disabled={isLoading}
                        />
                        <div className="f3__actions">
                          <button
                            className="f3__btn f3__btn--reservation"
                            type="button"
                            onClick={handleShowConfirmDialog}
                            disabled={isLoading}
                          >
                            {t('shareDevice.userInfo.buttons.reserve')}
                          </button>
                          <button
                            className="f3__btn f3__btn--back"
                            type="button"
                            onClick={handleBackToPreviousPage}
                            disabled={isLoading}
                          >
                            {t('shareDevice.userInfo.buttons.back')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <ShareDeviceSidebar />
      </div>

      {/* Modal - Confirm Email */}
      <ShareDeviceReservationDialog
        className="dialog__confirm-email"
        ref={confirmDialog.dialogRef}
      >
        <ShareDeviceConfirmEmailDialogContent
          email={form.email}
          onClose={confirmDialog.close}
          onConfirm={handleReservation}
        />
      </ShareDeviceReservationDialog>

      {/* Modal - Success */}
      <ShareDeviceReservationDialog
        className="dialog__success"
        ref={successDialog.dialogRef}
      >
        <ShareDeviceReservationSuccessDialogContent
          onConfirm={handleSuccessDialogConfirm}
        />
      </ShareDeviceReservationDialog>

      {/* Modal - Fully Booked */}
      <ShareDeviceReservationDialog
        className="dialog__fully-booked"
        ref={fullyBookedDialog.dialogRef}
      >
        <ShareDeviceDialogContentFullyBooked
          onConfirm={handleFullyBookedDialogConfirm}
        />
      </ShareDeviceReservationDialog>

      {/* タイムアウト警告通知 */}
      <SharedDeviceTimeoutNotification
        remainingSeconds={remainingSeconds}
        onContinue={resetTimer}
      />
    </ShareDeviceLayout>
  );
};
